import {
  ChatMessage,
  ChatService,
  getRandomConversationStarters,
  getSupabaseClient,
  useAppSelector,
} from '@lumora/common';
import type { Link, Root, Text } from 'mdast';
import { findAndReplace } from 'mdast-util-find-and-replace';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import type { Plugin } from 'unified';

import { ChatChart } from './ChatChart';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  tickers?: string[];
  error?: string;
  thinking?: string;
  chart?: {
    type: 'line' | 'bar' | 'pie';
    title: string;
    data: Array<{ label: string; value: number }>;
  };
}

export const AIChatbot: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content:
        "Hi! I'm your AI Portfolio Assistant. I can help you analyze your investments, track your goals, and provide personalized financial advice based on your portfolio data. What would you like to know?",
      timestamp: new Date(),
      suggestions: getRandomConversationStarters(4),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressEvents, setProgressEvents] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const chatServiceRef = useRef<ChatService | null>(null);

  const handleTickerClick = useCallback((ticker: string) => {
    if (!ticker) return;
    const url = `/ticker/${ticker}`;
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.blur();
      window.focus();
    }
  }, []);

  const markdownComponents = useMemo(
    () => ({
      a: (props: React.ComponentPropsWithoutRef<'a'>) => {
        const { href, children, ...anchorProps } = props;
        if (href?.startsWith('ticker:')) {
          const ticker = href.replace('ticker:', '');
          return (
            <button
              type="button"
              className="ai-chatbot-inline-ticker"
              onClick={() => handleTickerClick(ticker)}
            >
              {children}
            </button>
          );
        }

        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            {...anchorProps}
          >
            {children}
          </a>
        );
      },
      table: (props: React.ComponentPropsWithoutRef<'table'>) => (
        <div className="ai-chatbot-table-wrapper">
          <table className="ai-chatbot-table" {...props} />
        </div>
      ),
      th: (props: React.ComponentPropsWithoutRef<'th'>) => (
        <th className="ai-chatbot-th" {...props} />
      ),
      td: (props: React.ComponentPropsWithoutRef<'td'>) => (
        <td className="ai-chatbot-td" {...props} />
      ),
      div: (props: React.ComponentPropsWithoutRef<'div'>) => {
        const { className, children, ...divProps } = props;

        if (className === 'chart-render' && children) {
          try {
            // Children might be an array or string
            const jsonString = Array.isArray(children)
              ? children.join('')
              : String(children);
            if (!jsonString.trim()) return null;

            const chartData = JSON.parse(jsonString);
            return (
              <ChatChart
                type={chartData.type}
                title={chartData.title}
                data={chartData.data}
              />
            );
          } catch (e) {
            console.error('Failed to render chart:', e);
            return null;
          }
        }

        return (
          <div className={className} {...divProps}>
            {children}
          </div>
        );
      },
    }),
    [handleTickerClick]
  );

  // Get user data from Redux store
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userId = useAppSelector((state: any) => state.auth?.user?.id);

  // Initialize chat service
  useEffect(() => {
    const initializeChatService = async () => {
      try {
        const supabase = getSupabaseClient();
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.access_token) {
          chatServiceRef.current = new ChatService(session.access_token);
        }
      } catch (err) {
        console.error('Failed to initialize chat service:', err);
        setError('Failed to connect to AI service. Please try again later.');
      }
    };

    if (userId) {
      initializeChatService();
    }
  }, [userId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    // Calculate if we are near the bottom (within 100px)
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      150;

    // If typing (streaming), only scroll if user is already near bottom to avoid "fighting" the scroll
    // Use 'auto' (instant) behavior for streaming to prevent shaky animation
    if (isTyping) {
      if (isNearBottom) {
        scrollToBottom('auto');
      }
    } else {
      // For new messages (not streaming), always scroll to bottom smoothly
      scrollToBottom('smooth');
    }
  }, [messages, isTyping]);

  const scrollToBottom = (behavior: 'auto' | 'smooth' = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const generateMessageId = () =>
    `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const handleSendMessage = async (
    content: string,
    cardOutput?: string,
    displayText?: string
  ) => {
    if (!content.trim() || !userId) return;

    const trimmed = content.trim();
    setProgressEvents([]);

    const userVisible = (displayText || trimmed).trim();

    const userMessage: Message = {
      id: generateMessageId(),
      role: 'user',
      content: userVisible,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setError(null);

    // Create a placeholder assistant message
    const assistantMsgId = generateMessageId();
    const assistantMessage: Message = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      thinking: '',
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      const supabase = getSupabaseClient();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error('Not authenticated');
      }

      const conversationHistory = buildConversationHistoryPayload(messages);

      const response = await fetch('http://localhost:8788/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: trimmed,
          conversationHistory,
          formatInstructions: cardOutput,
          userId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) throw new Error('No response body');

      let buffer = '';
      let fullAccumulatedText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            const eventMatch = line.match(/^event: (.+)$/m);
            const dataMatch = line.match(/^data: (.+)$/m);

            if (eventMatch && dataMatch) {
              const event = eventMatch[1];
              const dataStr = dataMatch[1];

              try {
                const data = JSON.parse(dataStr);

                if (event === 'stage') {
                  const label = describeStageProgress(data);
                  if (label) {
                    setProgressEvents(prev =>
                      prev[prev.length - 1] === label ? prev : [...prev, label]
                    );
                  }
                } else if (event === 'delta') {
                  fullAccumulatedText += data.content || '';
                  const parsed = parseStreamedContent(fullAccumulatedText);

                  // Update message content progressively as it streams
                  setMessages(prev =>
                    prev.map(msg =>
                      msg.id === assistantMsgId
                        ? {
                            ...msg,
                            content: parsed.content,
                            thinking: parsed.thinking,
                            tickers: extractTickerSymbols(parsed.content),
                          }
                        : msg
                    )
                  );
                } else if (event === 'error') {
                  throw new Error(data.message);
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
        }
      }

      // Stream finished - show full response
      const finalParsed = parseStreamedContent(fullAccumulatedText);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === assistantMsgId
            ? {
                ...msg,
                content: finalParsed.content,
                thinking: finalParsed.thinking,
                suggestions: finalParsed.suggestions,
                chart: finalParsed.chart,
                tickers: extractTickerSymbols(finalParsed.content),
              }
            : msg
        )
      );
      setProgressEvents([]);
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === assistantMsgId
            ? {
                ...msg,
                error: err instanceof Error ? err.message : 'Unknown error',
              }
            : msg
        )
      );
    } finally {
      setIsTyping(false);
      setProgressEvents([]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(suggestion);
  };

  return (
    <div className="ai-chatbot-container">
      <style dangerouslySetInnerHTML={{ __html: chatbotStyles }} />

      {/* Error Banner */}
      {error && (
        <div className="ai-chatbot-error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
          {error}
        </div>
      )}

      {/* Messages */}
      <div className="ai-chatbot-messages" ref={chatContainerRef}>
        {messages.map(message => (
          <div
            key={message.id}
            className={`ai-chatbot-message ai-chatbot-message-${message.role}`}
          >
            {/* Only show message content if it's not empty. Thinking is shown in progress area. */}
            {(message.content || message.chart || message.error) && (
              <div className="ai-chatbot-message-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, tickerLinkRemarkPlugin]}
                  rehypePlugins={[rehypeRaw]}
                  components={markdownComponents}
                >
                  {message.content || ''}
                </ReactMarkdown>
                {message.error && (
                  <div className="ai-chatbot-message-error">
                    Error: {message.error}
                  </div>
                )}
              </div>
            )}
            <div className="ai-chatbot-message-time">
              {message.timestamp.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
            {message.role === 'assistant' &&
              message.tickers &&
              message.tickers.length > 0 && (
                <div className="ai-chatbot-tickers">
                  <span className="ai-chatbot-tickers-label">
                    Tickers mentioned
                  </span>
                  <div className="ai-chatbot-ticker-list">
                    {message.tickers.map(ticker => (
                      <button
                        key={ticker}
                        type="button"
                        className="ai-chatbot-ticker-button"
                        onClick={() => handleTickerClick(ticker)}
                      >
                        ${ticker}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            {message.suggestions && message.suggestions.length > 0 && (
              <div className="ai-chatbot-suggestions">
                {message.suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    className="ai-chatbot-suggestion"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        {isTyping &&
          (progressEvents.length > 0 ||
            (messages[messages.length - 1]?.thinking &&
              !messages[messages.length - 1]?.content)) && (
            <div className="ai-chatbot-progress">
              {progressEvents.map((stage, index) => (
                <div
                  key={`${stage}-${index}`}
                  className="ai-chatbot-progress-step"
                >
                  {stage}
                </div>
              ))}
              {messages[messages.length - 1]?.thinking &&
                !messages[messages.length - 1]?.content && (
                  <div className="ai-chatbot-progress-thinking">
                    <div className="ai-chatbot-thinking-header">
                      Thinking Process
                    </div>
                    <div className="ai-chatbot-thinking-content">
                      {messages[messages.length - 1].thinking}
                    </div>
                  </div>
                )}
            </div>
          )}

        {isTyping && progressEvents.length === 0 && (
          <div className="ai-chatbot-message ai-chatbot-message-assistant">
            <div className="ai-chatbot-typing">
              <div className="ai-chatbot-typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="ai-chatbot-typing-text">AI is thinking...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="ai-chatbot-input">
        <input
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask me about your portfolio..."
          disabled={isTyping}
          className="ai-chatbot-input-field"
        />
        <button
          onClick={() => handleSendMessage(inputValue)}
          disabled={!inputValue.trim() || isTyping}
          className="ai-chatbot-send-button"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const describeStageProgress = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const data = payload as {
    id?: string;
    iteration?: number;
    tool?: string;
    symbols?: string[];
  };

  switch (data.id) {
    case 'validating_request':
      return 'Validating your request…';
    case 'loading_tools':
      return 'Loading available AI tools…';
    case 'portfolio_context':
      return 'Preparing your portfolio context…';
    case 'market_context':
      return 'Fetching live market data…';
    case 'market_context_ready':
      return data.symbols?.length
        ? `Live quotes ready: ${data.symbols.join(', ')}`
        : 'Live market data ready';
    case 'llm_call':
      return `Generating response (pass ${data.iteration || 1})…`;
    case 'tool_routing':
    case 'tool_call':
      return data.tool
        ? `Calling ${data.tool.replace(/_/g, ' ')}…`
        : 'Calling AI tool…';
    case 'completed':
      return 'Finalizing response…';
    default:
      return null;
  }
};

const tickerLinkRemarkPlugin: Plugin = () => tree => {
  findAndReplace(
    tree as Root,
    [
      [
        /`([A-Z]{1,5})(?:\.[A-Z]{1,2})?`/g,
        (_fullMatch: string, symbol: string) =>
          ({
            type: 'link',
            url: `ticker:${symbol}`,
            children: [{ type: 'text', value: `$${symbol}` } as Text],
          }) as Link,
      ],
    ],
    { ignore: ['link', 'linkReference', 'code', 'inlineCode'] }
  );
};

function buildConversationHistoryPayload(messages: Message[]): ChatMessage[] {
  return messages
    .filter(msg => msg.role === 'user' || msg.role === 'assistant')
    .slice(-10)
    .map(msg => ({
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
    }));
}

function extractTickerSymbols(text: string): string[] {
  if (!text) {
    return [];
  }

  const normalized = text.toUpperCase();
  const detected = new Set<string>();
  let match: RegExpExecArray | null;

  // Only match `SYMBOL` patterns where SYMBOL is 1-5 uppercase letters
  // Exclude if followed by digits/decimals (to skip `2.07M`, `103K`, etc.)
  const backtickRegex =
    /`([A-Z]{1,5})(?:\.[A-Z]{1,2})?`(?![.\d]|K\b|M\b|B\b)\b/g;
  while ((match = backtickRegex.exec(normalized)) !== null) {
    if (match[1] && match[1].length >= 1) {
      detected.add(match[1]);
    }
  }
  return Array.from(detected.values());
}

interface ParsedContent {
  thinking?: string;
  content: string;
  suggestions?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chart?: any;
}

const parseStreamedContent = (fullText: string): ParsedContent => {
  let thinking = '';
  let content = fullText;
  let suggestions: string[] | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let chart: any | undefined;

  // Extract thinking
  const thinkingMatch = content.match(/<thinking>([\s\S]*?)<\/thinking>/);
  if (thinkingMatch) {
    thinking = thinkingMatch[1].trim();
    content = content.replace(/<thinking>[\s\S]*?<\/thinking>/, '').trim();
  } else {
    // Handle incomplete thinking tag
    const openThinking = content.match(/<thinking>([\s\S]*)/);
    if (openThinking) {
      thinking = openThinking[1].trim();
      content = content.replace(/<thinking>[\s\S]*/, '').trim();
    }
  }

  // Extract suggestions
  const suggestionsMatch = content.match(/```json:suggestions\s*([\s\S]*?)```/);
  if (suggestionsMatch) {
    try {
      suggestions = JSON.parse(suggestionsMatch[1]);
      content = content.replace(/```json:suggestions\s*[\s\S]*?```/, '').trim();
    } catch {
      // incomplete json
    }
  }

  // Extract chart
  const chartMatch = content.match(/```json:chart\s*([\s\S]*?)```/);
  if (chartMatch) {
    try {
      chart = JSON.parse(chartMatch[1]);
      // Remove the original json:chart block
      content = content.replace(/```json:chart\s*[\s\S]*?```/, '').trim();

      // Use a div with a specific class for rendering the chart
      // We use a div instead of a code block to avoid default pre/code styling
      const chartBlock = `\n<div class="chart-render">${JSON.stringify(chart)}</div>\n`;

      // Check for "Quick illustration" text to replace
      const illustrationRegex =
        /(\*\*Quick illustration.*?\*\*|Quick illustration.*?\n|Quick illustration \(.*?\))/i;
      if (illustrationRegex.test(content)) {
        content = content.replace(illustrationRegex, chartBlock);
      } else {
        // If no placeholder text found, append to end
        content += chartBlock;
      }
    } catch {
      // incomplete json
    }
  }

  // Clean up unwanted text
  content = content
    .replace(
      /(?:\*\*|#+\s*)?Quick visual of your current allocation.*?(?:\*\*|:)?(?:\n|$)/gi,
      ''
    )
    .replace(
      /(?:\*\*|#+\s*)?(?:Suggested\s+)?(?:\d*\s*)?follow[- ]up questions.*?(?:\*\*|:)?(?:\n|$)/gi,
      ''
    )
    .trim();

  return { thinking, content, suggestions, chart };
};

const chatbotStyles = `
  .ai-chatbot-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  }

  .ai-chatbot-error {
    padding: 12px 20px;
    background: #fef2f2;
    border-bottom: 1px solid #fecaca;
    color: #dc2626;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
  }

  .ai-chatbot-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    scroll-behavior: smooth;
    min-height: 0;
  }

  .ai-chatbot-messages::-webkit-scrollbar {
    width: 6px;
  }

  .ai-chatbot-messages::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  .ai-chatbot-messages::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  .ai-chatbot-messages::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  .ai-chatbot-message {
    display: flex;
    flex-direction: column;
    max-width: 85%;
    animation: fadeIn 0.3s ease-in;
    position: relative;
  }

  .ai-chatbot-message-user {
    align-self: flex-end;
    align-items: flex-end;
  }

  .ai-chatbot-message-assistant {
    align-self: flex-start;
    align-items: flex-start;
  }

  .ai-chatbot-message-content {
    padding: 14px 18px;
    border-radius: 18px;
    font-size: 15px;
    line-height: 1.5;
    word-wrap: break-word;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  }

  .ai-chatbot-message-user .ai-chatbot-message-content {
    background: #2563eb;
    color: white;
    border-bottom-right-radius: 4px;
  }

  .ai-chatbot-message-assistant .ai-chatbot-message-content {
    background: #f3f4f6;
    color: #1f2937;
    border-bottom-left-radius: 4px;
  }

  .ai-chatbot-message-time {
    font-size: 11px;
    color: #94a3b8;
    margin-top: 6px;
    padding: 0 4px;
  }

  .ai-chatbot-progress {
    margin: 0 0 8px 0;
    padding: 12px 16px;
    background: #f0f9ff;
    border-radius: 12px;
    font-size: 13px;
    color: #0369a1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border: 1px solid #bae6fd;
  }

  .ai-chatbot-progress-step {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .ai-chatbot-progress-step::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #0ea5e9;
    box-shadow: 0 0 0 2px #e0f2fe;
  }

  .ai-chatbot-message-content p {
    margin: 0 0 10px 0;
  }

  .ai-chatbot-message-content p:last-child {
    margin-bottom: 0;
  }

  .ai-chatbot-message-content h1,
  .ai-chatbot-message-content h2,
  .ai-chatbot-message-content h3,
  .ai-chatbot-message-content h4,
  .ai-chatbot-message-content h5,
  .ai-chatbot-message-content h6 {
    margin: 16px 0 8px 0;
    font-weight: 700;
    line-height: 1.3;
  }

  .ai-chatbot-message-user .ai-chatbot-message-content h1,
  .ai-chatbot-message-user .ai-chatbot-message-content h2,
  .ai-chatbot-message-user .ai-chatbot-message-content h3,
  .ai-chatbot-message-user .ai-chatbot-message-content h4,
  .ai-chatbot-message-user .ai-chatbot-message-content h5,
  .ai-chatbot-message-user .ai-chatbot-message-content h6 {
    color: white;
  }

  .ai-chatbot-message-content h1 { font-size: 18px; }
  .ai-chatbot-message-content h2 { font-size: 16px; }
  .ai-chatbot-message-content h3 { font-size: 15px; }

  .ai-chatbot-message-content ul,
  .ai-chatbot-message-content ol {
    margin: 8px 0;
    padding-left: 24px;
  }

  .ai-chatbot-message-content li {
    margin: 4px 0;
  }

  .ai-chatbot-message-content blockquote {
    margin: 12px 0;
    padding: 8px 16px;
    border-left: 4px solid #cbd5e1;
    background: rgba(255, 255, 255, 0.5);
    font-style: italic;
    border-radius: 0 8px 8px 0;
  }

  .ai-chatbot-message-user .ai-chatbot-message-content blockquote {
    border-left-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.1);
  }

  .ai-chatbot-message-content code {
    background: rgba(0, 0, 0, 0.06);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
  }

  .ai-chatbot-message-user .ai-chatbot-message-content code {
    background: rgba(255, 255, 255, 0.2);
  }

  .ai-chatbot-message-content pre {
    background: #1e293b;
    color: #e2e8f0;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 12px 0;
  }

  .ai-chatbot-message-content pre code {
    background: none;
    padding: 0;
    color: inherit;
  }

  .ai-chatbot-message-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
    font-size: 13px;
    background: white;
    border-radius: 8px;
    overflow: hidden;
  }

  .ai-chatbot-message-user .ai-chatbot-message-content table {
    color: #1f2937;
  }

  .ai-chatbot-message-content th,
  .ai-chatbot-message-content td {
    border: 1px solid #e5e7eb;
    padding: 8px 12px;
    text-align: left;
  }
  
  .ai-chatbot-message-content a {
    color: #2563eb;
    text-decoration: underline;
  }
  
  .ai-chatbot-message-user .ai-chatbot-message-content a {
    color: white;
    text-decoration: underline;
  }

  .ai-chatbot-message-content th {
    background: #f8f9fa;
    font-weight: 600;
  }

  .ai-chatbot-progress-thinking {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e2e8f0;
    animation: fadeIn 0.3s ease-in;
  }

  .ai-chatbot-thinking-header {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .ai-chatbot-thinking-header::before {
    content: '';
    width: 8px;
    height: 8px;
    border: 2px solid #cbd5e1;
    border-top-color: #64748b;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .ai-chatbot-thinking-content {
    font-size: 13px;
    color: #475569;
    font-style: italic;
    line-height: 1.4;
    white-space: pre-wrap;
    font-family: monospace;
  }

  .ai-chatbot-input {
    padding: 20px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    gap: 12px;
    background: white;
  }

  .ai-chatbot-input-field {
    flex: 1;
    padding: 12px 16px;
    border: 1px solid #d1d5db;
    border-radius: 24px;
    outline: none;
    font-size: 14px;
    transition: border-color 0.2s;
  }

  .ai-chatbot-input-field:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  .ai-chatbot-input-field:disabled {
    background: #f9fafb;
    cursor: not-allowed;
  }

  .ai-chatbot-send-button {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #3b82f6;
    color: white;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
  }

  .ai-chatbot-send-button:hover:not(:disabled) {
    background: #2563eb;
    transform: scale(1.05);
  }

  .ai-chatbot-send-button:disabled {
    background: #d1d5db;
    cursor: not-allowed;
    transform: none;
  }

  .ai-chatbot-tickers {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .ai-chatbot-tickers-label {
    font-size: 11px;
    color: #475569;
    font-weight: 600;
  }

  .ai-chatbot-ticker-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .ai-chatbot-ticker-button {
    padding: 4px 10px;
    border-radius: 14px;
    border: none;
    background: #e0f2fe;
    color: #0369a1;
    font-size: 12px;
    cursor: pointer;
    transition: background 0.2s, transform 0.2s;
  }

  .ai-chatbot-ticker-button:hover {
    background: #bae6fd;
    transform: translateY(-1px);
  }

  .ai-chatbot-inline-ticker {
    border: none;
    background: transparent;
    color: #2563eb;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
  }

  .ai-chatbot-suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  .ai-chatbot-suggestion {
    padding: 6px 12px;
    background: #e5e7eb;
    border: none;
    border-radius: 16px;
    font-size: 12px;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;
  }

  .ai-chatbot-suggestion:hover {
    background: #d1d5db;
    transform: translateY(-1px);
  }

  .ai-chatbot-typing {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #f3f4f6;
    border-radius: 18px;
    color: #6b7280;
  }

  .ai-chatbot-typing-dots {
    display: flex;
    gap: 4px;
  }

  .ai-chatbot-typing-dots span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #9ca3af;
    animation: typing 1.4s infinite ease-in-out;
  }

  .ai-chatbot-typing-dots span:nth-child(2) {
    animation-delay: 0.2s;
  }

  .ai-chatbot-typing-dots span:nth-child(3) {
    animation-delay: 0.4s;
  }

  .ai-chatbot-typing-text {
    font-size: 14px;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @keyframes typing {
    0%, 60%, 100% { transform: translateY(0); }
    30% { transform: translateY(-10px); }
  }

  .ai-chatbot-table-wrapper {
    overflow-x: auto;
    margin: 12px 0;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }

  .ai-chatbot-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  .ai-chatbot-th {
    background: #f3f4f6;
    padding: 10px 12px;
    text-align: left;
    font-weight: 600;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;
  }

  .ai-chatbot-td {
    padding: 10px 12px;
    border-bottom: 1px solid #f3f4f6;
    color: #4b5563;
  }

  .ai-chatbot-table tr:last-child .ai-chatbot-td {
    border-bottom: none;
  }

  .ai-chatbot-table tr:hover {
    background: #f9fafb;
  }
`;
